import { useState, useEffect, useRef } from "react";
import { toPng } from "html-to-image";
import styles from "./CertificateModal.module.css";
import { IoClose } from "react-icons/io5";
import BookMarkIcon from "../../../../icon/BookMarkIcon";
import { env } from "../../../../constants/env";
import axios from "axios";

const MobileCertificate = ({ onClose, selectedOverlay, setSelectedOverlay, downloading, overlays, desktopRef }) => {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagesReady, setImagesReady] = useState(false);

  const certificateRef = useRef(null);

  /* ---------------------------------------------
     Ensure all images inside certificate are loaded
  ---------------------------------------------- */
  const waitForImages = async () => {
    const node = certificateRef.current;
    if (!node) return;

    const images = node.querySelectorAll("img");

    await Promise.all(
      Array.from(images).map(async (img) => {
        if (!img.complete || img.naturalWidth === 0) {
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
        }
        if (img.decode) {
          await img.decode().catch(() => {});
        }
      })
    );
  };

  /* ---------------------------------------------
     Load leaderboard (same as Desktop)
  ---------------------------------------------- */
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const token = localStorage.getItem("userToken");

        const res = await axios.get(`${env.apiUrl}/api/leaderboard/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setLeaderboard(res.data);
      } catch (err) {
        console.error("Failed to load leaderboard", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  /* ---------------------------------------------
     Wait for images after data loads
  ---------------------------------------------- */
  useEffect(() => {
    if (!loading && leaderboard && certificateRef.current) {
      waitForImages()
        .then(() => setImagesReady(true))
        .catch(() => setImagesReady(true));
    }
  }, [loading, leaderboard]);

  /* ---------------------------------------------
     Re-wait when overlay changes
  ---------------------------------------------- */
  useEffect(() => {
    if (!certificateRef.current) return;

    setImagesReady(false);

    waitForImages()
      .then(() => setImagesReady(true))
      .catch(() => setImagesReady(true));
  }, [selectedOverlay]);

  if (loading) {
    return (
      <div className={styles.backdrop}>
        <div className={styles.loader}>Preparing certificate…</div>
      </div>
    );
  }

  const trader = leaderboard?.trader;
  const rank = leaderboard?.rank || "—";
  const total = leaderboard?.total || leaderboard?.fieldSize || "—";

  const status =
    leaderboard?.status === "FUNDED"
      ? "FUNDED"
      : leaderboard?.status === "FAILED"
      ? "FAILED"
      : leaderboard?.status === "YET TO TRADE"
      ? "YET TO TRADE"
      : "IN PROGRESS";

  const statusColor =
    status === "FAILED"
      ? "#FF8383"
      : status === "YET TO TRADE"
      ? "#B0B0B0"
      : status === "IN PROGRESS"
      ? "#FFB370"
      : "var(--primary-color)";

  /* ---------------------------------------------
     Download certificate
  ---------------------------------------------- */
const downloadFromDesktop = () => {
  desktopRef.current?.download();
};

  return (
    <div className={styles.backdrop}>
      {/* CLOSE */}
      <button className={styles.closeBtn} onClick={onClose}>
        <IoClose size={20} />
      </button>

      {/* LOADING OVERLAY */}
      {!imagesReady && (
        <div className={styles.imageLoader}>
          <div className="spinner-border" role="status" />
        </div>
      )}

      {/* CERTIFICATE */}
      <div
        ref={certificateRef}
        className={`mx-3 ${styles.certificate}`}
        style={{
          opacity: imagesReady ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      >
        <p className={styles.title}>PROPFUNDING.COM CHALLENGE CERTIFICATE</p>

        <div className="mt-4">
          <p className={styles.label}>TRADER</p>
          <h5 className={styles.value}>{trader}</h5>
        </div>

        <div className="mt-4">
          <p className={styles.label}>RANK</p>
          <div className={styles.rank}>
            <span>#{rank}</span>
            <h4 className={styles.value}>out of {total}</h4>
          </div>
        </div>

        <div className="mt-4">
          <p className={styles.label}>FUNDED STATUS</p>
          <h2 className={styles.passed} style={{ color: statusColor }}>
            {status}
          </h2>
        </div>

        <div className="mt-4">
          <p className={styles.tagline}>Pass First, Then Pay!</p>
          <p className={styles.subText}>
            Pass the challenge, pay the activation fee, keep up to 80% of profits.
          </p>
        </div>

        {/* OVERLAY */}
        <div className={styles.overlayPreview}>
          <img
            src={overlays.find((o) => o.id === selectedOverlay)?.image}
            alt="Overlay"
          />
        </div>
      </div>

      {/* CONTROLS */}
      <div
        className="px-3 mt-4"
        style={{
          opacity: imagesReady ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      >
         {/* <h6 className={`${styles.sectionTitle} mb-3`}>Customize your text</h6>
        <textarea
          className={styles.textarea}
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
        /> */}
        <h6 className={`${styles.sectionTitle} mt-4 mb-3`}>Overlay</h6>

        <div className="row g-3">
          {overlays.map((overlay) => (
            <div className="col-6" key={overlay.id}>
              <div
                className={`${styles.overlayOption} ${
                  selectedOverlay === overlay.id ? styles.active : ""
                }`}
                onClick={() => setSelectedOverlay(overlay.id)}
              >
                <img
                  src={overlay.image}
                  alt={overlay.label}
                  className={styles.overlayThumb}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 d-grid gap-3">
         <button
            className={styles.outlineBtn}
            onClick={downloadFromDesktop}
            disabled={!imagesReady || downloading}
          >
            {downloading ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <BookMarkIcon />
            )}
            <span>{downloading ? "Downloading…" : "Save Image"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileCertificate;